import { type RefObject } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { mobileTabsForRole, type NavItem } from '../nav';
import { useSession } from '../session';
import { IconMore } from '../icons';
import { AlertCountBadge, AlertCountWords, useAlertCount } from '../alerts/AlertCount';
import { useSidebar } from '@/components/ui/sidebar';

/**
 * The phone chrome (116 / 115-A2): a pinned bar of **Today · Schedule · Jobs · More**,
 * below `lg`.
 *
 * ⚠️ This REPLACES the office shell's mobile chrome; it does not join it. The hamburger
 * that used to sit in the top bar is gone, and its drawer — the whole sidebar, Settings,
 * theme and the running timer chip — is what **More** opens. Two controls opening the same
 * drawer on a 390px screen is the "second chrome" the ticket warns about, so `openerRef`
 * moved here rather than being copied.
 *
 * ⭐ Every item comes from `mobileTabsForRole`, which reads the same `zonesForRole` the
 * sidebar reads. The bar cannot show a role a door the sidebar hides, and the lockstep test
 * asserts the partition rather than trusting this file.
 */
export function MobileTabBar({
  openerRef,
}: {
  openerRef: RefObject<HTMLButtonElement | null>;
}) {
  const session = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { openMobile, toggleSidebar, isMobile } = useSidebar();
  const { tabs } = mobileTabsForRole(session.role);
  // Only the shown bar polls the Alerts count; at a desktop the sidebar's copy does.
  const alertCount = useAlertCount(isMobile);

  return (
    <nav
      aria-label="Primary"
      /**
       * `fixed`, not `sticky`: the bar has to hold the bottom edge of the VIEWPORT on a
       * page of any length, and a sticky element only holds an edge while its container is
       * in view. `z-30` matches the top bar and sits above `TableScrollRail`'s z-10 — which
       * is why the rail lifts by `--sw-bottom-chrome` instead of hiding under this.
       *
       * ⚠️ The inset lives in the bar's OWN `padding-bottom`, so the background paints all
       * the way to the hardware edge while the buttons sit clear of the home indicator.
       * Insetting the bar itself would leave a strip of page showing beneath it.
       */
      className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-border bg-surface lg:hidden"
      /* The border width comes from the same variable `--sw-bottom-chrome` sums, so the
         rule the eye sees and the height the page reserves cannot disagree. They did:
         the first measured run put the scroll rail 1px inside this bar. */
      style={{
        paddingBottom: 'var(--sw-safe-bottom)',
        borderTopWidth: 'var(--sw-tabbar-border)',
      }}
    >
      {tabs.map((item) => (
        <TabLink key={item.to} item={item} pathname={pathname} />
      ))}
      <button
        ref={openerRef}
        type="button"
        onClick={toggleSidebar}
        aria-expanded={openMobile}
        aria-haspopup="dialog"
        className={TAB_CLASS}
      >
        {/* Alerts live under More on a phone, so their count is drawn here too (Phase 7):
            on the icon, with the words inside the button for a screen reader. */}
        <span className="relative">
          <IconMore size={20} className="text-muted-foreground" />
          <AlertCountBadge count={alertCount} className="absolute -right-2 -top-1.5" />
        </span>
        <span>
          More
          <AlertCountWords count={alertCount} />
        </span>
      </button>
    </nav>
  );
}

/**
 * `min-h-[var(--sw-tabbar-h)]` — 56px, holding the measured 52px of tap target with room
 * above WCAG 2.5.5's 44px floor and the repo's own PhotoGrid minimum. The height is a
 * variable because the page's bottom padding is computed from the same one; two numbers
 * that must agree should not be typed twice.
 */
const TAB_CLASS =
  'flex min-h-[var(--sw-tabbar-h)] flex-col items-center justify-center gap-0.5 ' +
  'px-1 py-1.5 text-[11px] font-medium text-muted-foreground';

function TabLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const active = pathname === item.to || pathname.startsWith(item.to + '/');
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      /* The active identity is the brand colour and the weight, not a pill: a filled pill
         inside a 56px bar leaves the label nowhere to sit. `aria-current` carries the same
         fact to a screen reader, so the colour is never the only signal. */
      className={TAB_CLASS + (active ? ' font-semibold text-[var(--sw-brand-text)]' : '')}
    >
      <item.icon size={20} className={active ? 'text-[var(--sw-brand-text)]' : ''} />
      <span className="max-w-full truncate">{item.label}</span>
    </Link>
  );
}
